import React from 'react';
import { View, Text, StyleSheet, Animated as RNAnimated, TouchableOpacity, Alert } from 'react-native';
import ModernCard from '../ModernCard/ModernCard';
import { useTheme } from '~contexts/ThemeContext';
import { spacing, borderRadius, typography } from '~constants/design';
import Animated, { 
  FadeInDown, 
  useAnimatedStyle, 
  withSpring, 
  useSharedValue, 
  withTiming,
  interpolateColor
} from 'react-native-reanimated';

interface SocialPulseScoreProps {
  score: number;
  breakdown: {
    attendance: number;
    engagement: number;
    negative: number;
    streak: number;
  };
  lastUpdated?: string;
}

const SocialPulseScore: React.FC<SocialPulseScoreProps> = ({ 
  score, 
  breakdown,
  lastUpdated = 'Monday 8am GMT'
}) => {
  const { colors } = useTheme();
  const [isExpanded, setIsExpanded] = React.useState(false);
  
  const handleInfoPress = () => {
    Alert.alert(
      'Social Pulse Score',
      'Your Social Pulse reflects your community activity and reliability.\n\n' +
      '• Base: 50 points\n' +
      '• Attendance: Up to +30 pts\n' +
      '• Engagement: Up to +15 pts\n' +
      '• Streak: Up to +10 pts\n' +
      '• Penalties: Up to -20 pts\n\n' +
      'Calculated every Monday at 8am GMT.',
      [{ text: 'Got it' }]
    );
  };

  const getBandInfo = (s: number) => {
    if (s >= 80) return { 
      label: 'Thriving', 
      description: 'You\'re a community star! Your energy is inspiring and you\'re building wonderful connections.', 
      color: '#10B981', // Emerald 500
      emoji: '🔥'
    };
    if (s >= 60) return { 
      label: 'Healthy', 
      description: 'Great social energy! You\'re a valued part of the community and we love having you involved.', 
      color: '#3B82F6', // Blue 500
      emoji: '✨'
    };
    if (s >= 40) return { 
      label: 'Needs Attention', 
      description: 'We\'ve missed your presence! Every dinner is better with you—why not pick an event to join this week?', 
      color: '#F59E0B', // Amber 500
      emoji: '🌱'
    };
    return { 
      label: 'Inactive', 
      description: 'A warm welcome is always waiting for you! We\'d love to help you reconnect with the community whenever you\'re ready.', 
      color: '#EF4444', // Red 500
      emoji: '💤'
    };
  };

  const band = getBandInfo(score);
  const progressWidth = useSharedValue(0);

  React.useEffect(() => {
    progressWidth.value = withSpring(Math.max(0, Math.min(100, score)));
  }, [score]);

  const animatedProgressStyle = useAnimatedStyle(() => {
    return {
      width: `${progressWidth.value}%`,
      backgroundColor: band.color,
    };
  });

  return (
    <Animated.View entering={FadeInDown.delay(350).duration(500)}>
      <ModernCard 
        variant="elevated" 
        padding="lg" 
        margin="md" 
        onPress={() => setIsExpanded(!isExpanded)}
      >
        <View style={styles.header}>
          <View style={{ flex: 1 }}>
            <View style={styles.titleRow}>
              <Text style={[styles.title, { color: colors.textPrimary }]}>
                Social Pulse Score
              </Text>
              {/* <TouchableOpacity onPress={handleInfoPress} style={styles.infoButton}>
                <Text style={[styles.infoIcon, { color: colors.textSecondary }]}>ⓘ</Text>
              </TouchableOpacity> */}
            </View>
            <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
              {isExpanded ? `Updated weekly • ${lastUpdated}` : 'Tap to see breakdown'}
            </Text>
          </View>
          <View style={styles.headerRight}>
            <View style={[styles.scoreBadge, { backgroundColor: band.color + '20' }]}>
              <Text style={[styles.scoreBadgeText, { color: band.color }]}>
                {band.emoji} {band.label}
              </Text>
            </View>
            <Text style={[styles.chevron, { color: colors.textSecondary }]}>
              {isExpanded ? '▲' : '▼'}
            </Text>
          </View>
        </View>

        <View style={styles.scoreContainer}>
          <View style={styles.scoreRow}>
            <Text style={[styles.scoreMain, { color: colors.textPrimary, fontSize: isExpanded ? typography.fontSize['6xl'] : typography.fontSize['4xl'] }]}>
              {score}
              <Text style={styles.scoreMax}> / 100</Text>
            </Text>
            {!isExpanded && (
              <View style={[styles.miniProgressBackground, { backgroundColor: colors.border + '30', flex: 1, marginLeft: spacing.lg, height: 8 }]}>
                <Animated.View style={[styles.miniProgressFill, animatedProgressStyle]} />
              </View>
            )}
          </View>
          
          {isExpanded && (
            <View style={[styles.progressBackground, { backgroundColor: colors.border + '30' }]}>
              <Animated.View style={[styles.progressFill, animatedProgressStyle]} />
            </View>
          )}
        </View>

        {isExpanded && (
          <Animated.View entering={FadeInDown.duration(300)}>
            <Text style={[styles.description, { color: colors.textSecondary }]}>
              {band.description}
            </Text>

            <View style={styles.divider} />

            <View style={styles.breakdownContainer}>
              <BreakdownItem 
                label="Streak Bonus" 
                value={breakdown.streak} 
                max={10} 
                color="#EC4899" 
                icon="⚡"
                textColor={colors.textPrimary}
                subTextColor={colors.textSecondary}
              />
            </View>
          </Animated.View>
        )}
      </ModernCard>
    </Animated.View>
  );
};

interface BreakdownItemProps {
  label: string;
  value: number;
  max: number;
  min?: number;
  color: string;
  icon: string;
  isNegative?: boolean;
  textColor: string;
  subTextColor: string;
}

const BreakdownItem: React.FC<BreakdownItemProps> = ({ 
  label, value, max, min = 0, color, icon, isNegative, textColor, subTextColor 
}) => {
  const displayValue = isNegative ? (value > 0 ? `-${value}` : value) : `+${value}`;
  const percentage = isNegative 
    ? (Math.abs(value) / Math.abs(min)) * 100 
    : (value / max) * 100;

  return (
    <View style={styles.breakdownItem}>
      <View style={styles.breakdownItemHeader}>
        <View style={styles.labelRow}>
          <Text style={styles.icon}>{icon}</Text>
          <Text style={[styles.breakdownLabel, { color: textColor }]}>{label}</Text>
        </View>
        <Text style={[styles.breakdownValue, { color: isNegative && value < 0 ? '#EF4444' : color }]}>
          {value > 0 && !isNegative ? '+' : ''}{value}
          <Text style={styles.breakdownMax}> / {isNegative ? min : max}</Text>
        </Text>
      </View>
      <View style={[styles.miniProgressBackground, { backgroundColor: '#E5E7EB30' }]}>
        <View 
          style={[
            styles.miniProgressFill, 
            { 
              width: `${Math.min(100, percentage)}%`, 
              backgroundColor: isNegative && value < 0 ? '#EF4444' : color 
            }
          ]} 
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: spacing.sm,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  chevron: {
    fontSize: 12,
    marginLeft: spacing.sm,
    opacity: 0.5,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  title: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.bold,
  },
  infoButton: {
    marginLeft: spacing.xs,
    padding: spacing.xs,
  },
  infoIcon: {
    fontSize: 16,
    opacity: 0.8,
  },
  subtitle: {
    fontSize: typography.fontSize.xs,
    marginTop: 2,
  },
  scoreBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: borderRadius.full,
  },
  scoreBadgeText: {
    fontSize: 10,
    fontWeight: typography.fontWeight.semibold,
  },
  scoreContainer: {
    marginVertical: spacing.sm,
  },
  scoreRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  scoreMain: {
    fontWeight: typography.fontWeight.black,
    marginBottom: spacing.xs,
  },
  scoreMax: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.normal,
    opacity: 0.5,
  },
  progressBackground: {
    height: 12,
    borderRadius: 6,
    width: '100%',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 6,
  },
  description: {
    fontSize: typography.fontSize.sm,
    lineHeight: 20,
    marginBottom: spacing.md,
  },
  divider: {
    height: 1,
    backgroundColor: '#E5E7EB30',
    marginVertical: spacing.md,
  },
  breakdownTitle: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
    marginBottom: spacing.md,
  },
  breakdownContainer: {
    gap: spacing.md,
  },
  breakdownItem: {
    marginBottom: spacing.sm,
  },
  breakdownItemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  labelRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  icon: {
    fontSize: 16,
    marginRight: 8,
  },
  breakdownLabel: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
  },
  breakdownValue: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.bold,
  },
  breakdownMax: {
    fontSize: 12,
    fontWeight: typography.fontWeight.normal,
    opacity: 0.6,
  },
  miniProgressBackground: {
    height: 6,
    borderRadius: 3,
    width: '100%',
    overflow: 'hidden',
  },
  miniProgressFill: {
    height: '100%',
    borderRadius: 3,
  },
});

export default SocialPulseScore;
